import React, { useEffect, useRef } from 'react'
import { GET_PROPERTIES_QUERY_LIMIT } from '@/utils/pagination'
// Centralized lodash imports - much cleaner!
import {
  isEqual,
  isEmpty,
  isNil,
} from '@/utils/lodash'

function FetchMore(props) {
  const {
    loadMore = false,
    fetchMoreProperties,
    cursor = null,
  } = props

  const fetchMoreRef = useRef(fetchMoreProperties)
  fetchMoreRef.current = fetchMoreProperties
  const inFlightRef = useRef(false)
  const lastCursorRef = useRef(undefined)

  useEffect(() => {
    if (!loadMore || typeof fetchMoreRef.current !== 'function') {
      if (!loadMore) {
        lastCursorRef.current = undefined
        inFlightRef.current = false
      }
      return
    }

    // Avoid re-fetching the same page when parent re-renders with a new
    // fetchMore function identity (common after Apollo Client 4).
    if (inFlightRef.current || lastCursorRef.current === cursor) {
      return
    }

    inFlightRef.current = true
    lastCursorRef.current = cursor

    Promise.resolve(
      fetchMoreRef.current({
        variables: {
          cursor,
          limit: GET_PROPERTIES_QUERY_LIMIT,
        },
        updateQuery: (prevResult, { fetchMoreResult  }) => {
          if (!fetchMoreResult) {
            return prevResult
          }

          const isMarketplaceQuery =
            !isNil(prevResult?.marketplaceProperties) ||
            !isNil(fetchMoreResult?.marketplaceProperties)

          if (isMarketplaceQuery) {
            const prevMarketplace = prevResult?.marketplaceProperties
            const moreMarketplace =
              fetchMoreResult?.marketplaceProperties

            if (!moreMarketplace) {
              return prevResult
            }

            if (
              !isEqual(
                prevMarketplace?.marketplaces,
                moreMarketplace?.marketplaces,
              )
            ) {
              return prevResult
            } else if (
              isNil(moreMarketplace?.approved) !==
                isNil(prevMarketplace?.approved) ||
              isNil(moreMarketplace?.sold) !==
                isNil(prevMarketplace?.sold)
            ) {
              return prevResult
            } else if (
              moreMarketplace?.approved !== prevMarketplace?.approved ||
              moreMarketplace?.sold !== prevMarketplace?.sold
            ) {
              return prevResult
            }

            const prevProperties = Array.isArray(
              prevMarketplace?.properties,
            )
              ? prevMarketplace.properties
              : []
            const moreProperties = Array.isArray(
              moreMarketplace?.properties,
            )
              ? moreMarketplace.properties
              : []

            if (isEmpty(moreProperties)) {
              return prevResult
            }

            const byId = new Map()
            ;[...prevProperties, ...moreProperties].forEach((p) => {
              if (p && p._id) {
                byId.set(p._id, p)
              }
            })

            return {
              ...fetchMoreResult,
              marketplaceProperties: {
                ...moreMarketplace,
                properties: Array.from(byId.values()),
              },
            }
          }

          const prevPropertiesContainer = prevResult?.properties
          const morePropertiesContainer = fetchMoreResult?.properties

          if (!morePropertiesContainer) {
            return prevResult
          }

          const prevProperties = Array.isArray(
            prevPropertiesContainer?.properties,
          )
            ? prevPropertiesContainer.properties
            : []
          const moreProperties = Array.isArray(
            morePropertiesContainer?.properties,
          )
            ? morePropertiesContainer.properties
            : []

          if (isEmpty(moreProperties)) {
            return prevResult
          }

          const byId = new Map()
          ;[...prevProperties, ...moreProperties].forEach((p) => {
            if (p && p._id) {
              byId.set(p._id, p)
            }
          })

          return {
            ...fetchMoreResult,
            properties: {
              ...morePropertiesContainer,
              properties: Array.from(byId.values()),
            },
          }
        },
      }),
    )
      .catch((error) => {
        console.error('[FetchMore] failed', error)
        // Allow retry on the same cursor after a failure.
        lastCursorRef.current = undefined
      })
      .finally(() => {
        inFlightRef.current = false
      })
  }, [loadMore, cursor])

  return null
}

export default FetchMore
